// src/simulators/FirewallSimulator.jsx
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  sim: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  btn: {
    padding: theme.spacing(1, 2),
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 600,
  },
  addBtn: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  testBtn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: theme.spacing(1),
  },
  th: {
    backgroundColor: theme.palette.grey[700],
    color: 'white',
    padding: theme.spacing(1),
    textAlign: 'left',
  },
  td: {
    padding: theme.spacing(0.75),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  input: {
    width: '100%',
    padding: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    color: theme.palette.text.primary,
  },
  select: {
    padding: theme.spacing(0.5),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
  logs: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    minHeight: 60,
    fontFamily: 'monospace',
  },
  log: {
    padding: theme.spacing(0.5),
    borderBottom: '1px dashed #ccc',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  logSuccess: {
    color: theme.palette.success.main,   // green
  },
  logError: {
    color: theme.palette.error.main,     // red
  },
}));

const initialRules = [
  { id: 1, action: 'allow', src: 'any', dst: '10.10.10.0/24', port: 80, proto: 'tcp' },
  { id: 2, action: 'allow', src: 'any', dst: '10.10.10.0/24', port: 443, proto: 'tcp' },
];

const attackFlows = [
  { src: '10.20.1.100', dst: '10.10.10.25', port: 3389, proto: 'tcp', desc: 'RDP from DMZ' },
  { src: '10.10.10.25', dst: '10.10.30.50', port: 445, proto: 'tcp', desc: 'SMB lateral' },
  { src: '10.10.30.50', dst: '8.8.8.8', port: 53, proto: 'udp', desc: 'DNS legit' },
];

const FirewallSimulator = ({ step, onComplete }) => {
  const { classes } = useStyles();
  const [rules, setRules] = useState(initialRules);
  const [logs, setLogs] = useState([]);

  const addRule = () => {
    setRules(prevRules => [
      ...prevRules,
      {
        id: Date.now(),
        action: 'deny',
        src: '10.20.1.0/24',
        dst: 'any',
        port: '',
        proto: 'any'
      }
    ]);
  };

  const updateRule = (id, field, value) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };


  const evaluate = () => {
    const blockedLateral = attackFlows
      .filter(f => f.desc.includes('lateral'))
      .every(flow => rules.some(r =>
        r.action === 'deny' &&
        (r.src === 'any' || flow.src.startsWith(r.src.split('/')[0])) &&
        (r.dst === 'any' || flow.dst.startsWith(r.dst.split('/')[0])) &&
        (r.proto === 'any' || r.proto === flow.proto) &&
        (r.port === '' || r.port == flow.port)
      ));

    const webAllowed = attackFlows
      .filter(f => f.port === 80 || f.port === 443)
      .every(flow => rules.some(r => r.action === 'allow' && r.port == flow.port));

    if (blockedLateral && webAllowed) {
      setLogs(['All attacks blocked. Legitimate traffic allowed.']);
      setTimeout(() => onComplete(step.points), 1500);
    } else {
      setLogs(['Failed: Lateral movement not blocked or web broken.']);
    }
  };

  return (
    <div className={classes.sim}>
      <h3 className={classes.title}>Configure Firewall to Stop Lateral Movement</h3>
      <div className={classes.controls}>
        <button onClick={addRule} className={`${classes.btn} ${classes.addBtn}`}>Add Rule</button>
        <button onClick={evaluate} className={`${classes.btn} ${classes.testBtn}`}>Test Configuration</button>
      </div>

      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.th}>Action</th>
            <th className={classes.th}>Src</th>
            <th className={classes.th}>Dst</th>
            <th className={classes.th}>Port</th>
            <th className={classes.th}>Proto</th>
          </tr>
        </thead>
        <tbody>
          {rules.map(r => (
            <tr key={r.id}>
              <td className={classes.td}>
                <select
                  value={r.action}
                  onChange={e => updateRule(r.id, 'action', e.target.value)}
                  className={classes.select}
                >
                  <option value="allow">allow</option>
                  <option value="deny">deny</option>
                </select>
              </td>
              <td className={classes.td}><input className={classes.input} value={r.src} onChange={e => updateRule(r.id, 'src', e.target.value)} /></td>
              <td className={classes.td}><input className={classes.input} value={r.dst} onChange={e => updateRule(r.id, 'dst', e.target.value)} /></td>
              <td className={classes.td}><input className={classes.input} value={r.port} onChange={e => updateRule(r.id, 'port', e.target.value)} /></td>
              <td className={classes.td}><input className={classes.input} value={r.proto} onChange={e => updateRule(r.id, 'proto', e.target.value)} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={classes.logs}>
        {logs.map((l, i) => {
          const isSuccess = l.includes('All attacks blocked');
          const logClass = `${classes.log} ${isSuccess ? classes.logSuccess : classes.logError}`;
          return (
            <div key={i} className={logClass}>
              {l}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FirewallSimulator;